import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Globe, Menu, X, ExternalLink } from 'lucide-react';
import { categories } from '@/mocks/data';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group">
            <span className="text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
              AllChinaBuy<span className="text-primary-600">Spread</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setCategoriesOpen(!categoriesOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors"
              >
                {t('nav_categories')}
                <ChevronDown className={`w-4 h-4 transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoriesOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="p-2 max-h-[400px] overflow-y-auto">
                    {categories.map((cat, idx) => (
                      <a
                        key={idx}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors group"
                        onClick={() => setCategoriesOpen(false)}
                      >
                        <span className="w-2 h-2 rounded-full bg-primary-400 group-hover:bg-primary-500 transition-colors" />
                        <span className="flex-1 font-medium">{cat.name}</span>
                        <ExternalLink className="w-3.5 h-3.5 text-foreground-400 group-hover:text-foreground-600" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="/guides" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors">
              {t('nav_guides')}
            </a>
            <a href="/reviews" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors">
              {t('nav_reviews')}
            </a>
            <a href="/shipping" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors">
              {t('nav_shipping')}
            </a>
            <a href="/safety" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors">
              {t('nav_safety')}
            </a>
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs font-semibold">{currentLang.code}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-background-50 rounded-xl shadow-lg border border-background-200/70 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors ${
                        i18n.language === lang.code
                          ? 'text-primary-700 bg-primary-50 font-medium'
                          : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-1.5"
            >
              {t('nav_spreadsheet')}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70 animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-4 space-y-1">
            <div className="px-3 py-2 text-sm font-semibold text-foreground-500 uppercase tracking-wider">
              {t('nav_categories')}
            </div>
            {categories.map((cat, idx) => (
              <a
                key={idx}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                {cat.name}
                <ExternalLink className="w-3.5 h-3.5 ml-auto text-foreground-400" />
              </a>
            ))}
            <div className="border-t border-background-200/70 my-3" />
            <a href="/guides" className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              {t('nav_guides')}
            </a>
            <a href="/reviews" className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              {t('nav_reviews')}
            </a>
            <a href="/shipping" className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              {t('nav_shipping')}
            </a>
            <a href="/safety" className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              {t('nav_safety')}
            </a>
            <div className="border-t border-background-200/70 my-3" />
            <div className="px-3 py-2 text-sm font-semibold text-foreground-500 uppercase tracking-wider">
              {t('nav_language')}
            </div>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  setMobileMenuOpen(false);
                }}
                className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm rounded-lg transition-colors ${
                  i18n.language === lang.code
                    ? 'text-primary-700 bg-primary-50 font-medium'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                <span className="text-base">{lang.flag}</span>
                {lang.label}
              </button>
            ))}
            <div className="border-t border-background-200/70 my-3" />
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-background-50 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav_spreadsheet')}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}